import { VOICE_DISPLAY_NAMES, type VoiceKey } from "@/data/voiceDisplayNames";
import type { GeneratedTutorDraft, Tutor, TutorArchetype } from "@/types/tutor";

const VOICE_IMAGES: Record<VoiceKey, string> = {
  Aoede: "/ui/Tutors/Phoebe.png",
  Puck: "/ui/Tutors/Joshua.png",
  Charon: "/ui/Tutors/Ethan.png",
  Fenrir: "/ui/Tutors/Bryan.png",
  Kore: "/ui/Tutors/Sam.jpg",
  Leda: "/ui/Tutors/Joanna.png",
  Orus: "/ui/Tutors/Markus.png",
  Zephyr: "/ui/Tutors/Lisa.png",
};

const ARCHETYPE_IMAGES: Record<TutorArchetype, string> = {
  language_coach: "/ui/Tutors/Phoebe.png",
  exam_prep: "/ui/Tutors/Joanna.png",
  coding_mentor: "/ui/Tutors/Markus.png",
  roleplay_coach: "/ui/Tutors/Sam.jpg",
  subject_tutor: "/ui/Tutors/Joanna.png",
  interview_coach: "/ui/Tutors/Ethan.png",
  wellness_guide: "/ui/Yogi.png",
  custom: "/ui/Tutors/Funny_lisa.png",
};

const CATEGORY_IMAGES: Record<string, string> = {
  language: "/ui/Tutors/Phoebe.png",
  academic: "/ui/Tutors/Joanna.png",
  coding: "/ui/Tutors/Markus.png",
  roleplay: "/ui/Tutors/Sam.jpg",
  zen: "/ui/Yogi.png",
  wellness: "/ui/Yogi.png",
  general: "/ui/Tutors/Funny_lisa.png",
};

const NAME_IMAGES: Array<[RegExp, string]> = [
  [/k-?pop|music|pop culture|idol|fandom/i, "/ui/Tutors/Lisa.png"],
  [/dutch|vocab|vocabulary|language/i, "/ui/Celine.png"],
  [/english|speaking|conversation/i, "/ui/Tutors/Phoebe.png"],
  [/sales|cold|outreach|negotiation/i, "/ui/Tutors/Sam.jpg"],
  [/interview/i, "/ui/Tutors/Ethan.png"],
  [/meditation|mind|calm|zen|wellness/i, "/ui/Yogi.png"],
  [/piano|music theory|harmony/i, "/ui/Tutors/Joanna.png"],
  [/coding|code|debug/i, "/ui/Tutors/Markus.png"],
];

function normalizeVoice(input?: string | null): VoiceKey | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (trimmed in VOICE_IMAGES) return trimmed as VoiceKey;

  const entry = Object.entries(VOICE_DISPLAY_NAMES).find(
    ([voice, label]) =>
      voice.toLowerCase() === trimmed.toLowerCase() || label.toLowerCase() === trimmed.toLowerCase()
  );
  return entry ? (entry[0] as VoiceKey) : null;
}

function getNameBasedImage(
  name?: string | null,
  description?: string | null,
  goal?: string | null
) {
  const haystack = [name, description, goal].filter(Boolean).join(" ");
  const match = NAME_IMAGES.find(([pattern]) => pattern.test(haystack));
  return match?.[1] || null;
}

export function getTutorImageUrl(
  tutor: Pick<
    Tutor,
    "avatar_url" | "voice_id" | "archetype" | "category" | "name" | "description" | "goal"
  >
) {
  if (tutor.avatar_url?.trim()) return tutor.avatar_url.trim();

  const voice = normalizeVoice(tutor.voice_id);
  if (voice) return VOICE_IMAGES[voice];

  const nameImage = getNameBasedImage(tutor.name, tutor.description, tutor.goal);
  if (nameImage) return nameImage;

  if (tutor.archetype) return ARCHETYPE_IMAGES[tutor.archetype] || ARCHETYPE_IMAGES.custom;

  const category = tutor.category?.trim().toLowerCase();
  return (category && CATEGORY_IMAGES[category]) || ARCHETYPE_IMAGES.custom;
}

export function getTutorDraftImageUrl(draft: GeneratedTutorDraft, voiceId?: string | null) {
  const voice = normalizeVoice(voiceId);
  if (voice) return VOICE_IMAGES[voice];

  const nameImage = getNameBasedImage(draft.name, draft.description, draft.goal);
  if (nameImage) return nameImage;

  return (
    ARCHETYPE_IMAGES[draft.archetype] ||
    CATEGORY_IMAGES[draft.category.toLowerCase()] ||
    ARCHETYPE_IMAGES.custom
  );
}
